import * as R from 'ramda'
import { getQuizPage } from 'ducks/router'

// actions
export const FETCH_RESPONDENT = 'respondent/FETCH'
export const fetchRespondent = () => ({ type: FETCH_RESPONDENT })

export const RESET_RESPONDENT = 'respondent/RESET'
export const resetRespondent = () => ({ type: RESET_RESPONDENT })

export const UPDATE_RESPONDENT = 'respondent/UPDATE'
export const updateRespondent = payload => ({
  type: UPDATE_RESPONDENT,
  payload,
})

export const START_PERSONALITY = 'personality/START'
export const startPersonality = () => ({ type: START_PERSONALITY })

export const FINISH_PERSONALITY = 'personality/FINISH'
export const finishPersonality = () => ({ type: FINISH_PERSONALITY })

export const CHANGE_PERSONALITY_ANSWER = 'personality/CHANGE_ANSWER'
export const changePersonalityAnswer = ({ instrument, key, value }) => ({
  type: CHANGE_PERSONALITY_ANSWER,
  payload: { instrument, key, value },
})

export const START_INTEREST = 'interest/START'
export const startInterest = () => ({ type: START_INTEREST })

export const FINISH_INTEREST = 'interest/FINISH'
export const finishInterest = () => ({ type: FINISH_INTEREST })

export const CHANGE_INTEREST_ANSWER = 'interest/CHANGE_ANSWER'
export const changeInterestAnswer = ({ instrument, key, value }) => ({
  type: CHANGE_INTEREST_ANSWER,
  payload: { instrument, key, value },
})

export const START_GMA = 'gma/START'
export const startGMA = ({ reason }) => ({
  type: START_GMA,
  payload: { reason },
})

export const FINISH_GMA = 'gma/FINISH'
export const finishGMA = ({ reason }) => ({
  type: FINISH_GMA,
  payload: { reason },
})

export const SUBMIT_GMA = 'respondent/SUBMIT_GMA'
export const submitGMA = () => ({ type: SUBMIT_GMA })

export const SUBMIT_PERSONALITY = 'respondent/SUBMIT_PERSONALITY'
export const submitPersonality = () => ({ type: SUBMIT_PERSONALITY })

export const SUBMIT_INTEREST = 'respondent/SUBMIT_INTEREST'
export const submitInterest = () => ({ type: SUBMIT_INTEREST })

export const CHANGE_GMA_ANSWER = 'gma/CHANGE_ANSWER'
export const changeGMAAnswer = ({ key, value }) => ({
  type: CHANGE_GMA_ANSWER,
  payload: { key, value },
})

export const CHANGE_LANGUAGE = 'respondent/CHANGE_LANGUAGE'
export const changeLanguage = language => ({
  type: CHANGE_LANGUAGE,
  payload: { language },
})

export const DEADLINE_PASSED = 'respondent/DEADLINE_PASSED'
export const deadlinePassed = () => ({ type: DEADLINE_PASSED })

export const REUSE_CONSENT = 'assessment/REUSE_CONSENT'
export const reuseConsent = (consent, reuseTests) => ({
  type: REUSE_CONSENT,
  payload: { consent, ...(reuseTests ? { reuse_tests: reuseTests } : {}) },
})

// reducer
const initialState = {
  uuid: null,
  name: null,
  email: null,
  status: null,
  loading: true,
  has_previous_results: false,
  reuse_selection: null,
  language: 'nb',
  deadline: null,
  deadline_display: null,
  deadline_tz: null,
  past_deadline: false,
  access_blocked: false,
  personality: {
    status: 'fetching',
    answers: {},
  },
  gma: {
    status: 'fetching',
    answers: {},
  },
  interest: {
    status: 'fetching',
    answers: {},
  },
}

const toggleSubmit = status =>
  status === 'confirmsubmit' ? 'started' : 'confirmsubmit'

const countGMA = R.converge(R.assocPath(['stats', 'gma', 'totalAnswers']), [
  R.pipe(R.path(['gma', 'answers']), R.values, R.reject(R.isNil), R.length),
  R.identity,
])

const countPersonality = R.converge(
  R.assocPath(['stats', 'personality', 'totalAnswers']),
  [
    R.pipe(
      R.path(['personality', 'answers']),
      R.values,
      R.chain(R.values),
      R.reject(R.isNil),
      R.length,
    ),
    R.identity,
  ],
)

const countInterest = R.converge(
  R.assocPath(['stats', 'interest', 'totalAnswers']),
  [
    R.pipe(
      R.path(['interest', 'answers']),
      R.values,
      R.reject(R.isNil),
      R.length,
    ),
    R.identity,
  ],
)

export const reducer = (state = initialState, action) => {
  switch (action.type) {
    case CHANGE_LANGUAGE:
      return R.assoc('language', action.payload.language, state)
    case DEADLINE_PASSED:
      return R.pipe(
        R.assoc('past_deadline', true),
        R.assoc('access_blocked', true),
      )(state)
    case RESET_RESPONDENT:
      return R.mergeDeepLeft(initialState, state)
    case UPDATE_RESPONDENT:
      return R.assoc('loading', false, R.mergeDeepLeft(action.payload, state))
    case CHANGE_PERSONALITY_ANSWER: {
      const { instrument, key, value } = action.payload
      return R.pipe(
        R.assocPath(['personality', 'answers', instrument, key], value),
        countPersonality,
      )(state)
    }
    case CHANGE_INTEREST_ANSWER: {
      const { key, value } = action.payload
      return R.pipe(
        R.assocPath(['interest', 'answers', key], value),
        countInterest,
      )(state)
    }
    case CHANGE_GMA_ANSWER: {
      const { key, value } = action.payload
      return R.pipe(
        R.assocPath(['gma', 'answers', key], value),
        countGMA,
      )(state)
    }
    case SUBMIT_PERSONALITY:
      return R.over(R.lensPath(['personality', 'status']), toggleSubmit, state)
    case SUBMIT_GMA:
      return R.over(R.lensPath(['gma', 'status']), toggleSubmit, state)
    case SUBMIT_INTEREST:
      return R.over(R.lensPath(['interest', 'status']), toggleSubmit, state)
    default:
      return state
  }
}

// helper functions

// selectors
export const getRespondent = R.pipe(R.path(['respondent']))
export const getLanguage = R.pathOr('en', ['respondent', 'language'])

export const isAccessBlocked = R.pathOr(false, ['respondent', 'access_blocked'])

export const getGMA = R.converge(R.mergeRight, [
  R.path(['respondent', 'stats', 'gma']),
  R.path(['respondent', 'gma']),
])
export const getPersonality = R.converge(R.mergeRight, [
  R.path(['respondent', 'stats', 'personality']),
  R.path(['respondent', 'personality']),
])
export const getInterest = R.converge(R.mergeRight, [
  R.path(['respondent', 'stats', 'interest']),
  R.path(['respondent', 'interest']),
])

export const getGMAQuestion = props => state => {
  const { page = 0, uuid } = props || getQuizPage(state)
  const { answers } = R.path(['respondent', 'gma'], state)
  const [question, value] = R.toPairs(answers)[page]
  return { question, value, current: page, uuid }
}

export const getPersonalityQuestion = props => state => {
  const { page = 0, uuid } = props || getQuizPage(state)
  const { instruments, questions, answers } = R.path(
    ['respondent', 'personality'],
    state,
  )

  if (!instruments) return { label: '', scale: {} }

  const { instrument, key } = R.prop(page, questions)
  const { scale, prompt, questions: qx } = R.prop(instrument, instruments)
  const { label } = R.find(R.propEq('key', key), qx)
  const value = R.path([instrument, key], answers)

  const question = {
    uuid,
    instrument,
    question: key,
    prompt,
    label,
    scale,
    value,
    current: page,
  }

  return question
}

const emptyInterestQuestion = (uuid, page = 0) => ({
  label: '',
  scale: {},
  uuid,
  current: page,
})

export const getInterestQuestion = props => state => {
  const quizPage = props || getQuizPage(state)
  const page = Number.isFinite(quizPage.page) ? quizPage.page : 0
  const { uuid } = quizPage
  const { instruments, answers } = R.path(['respondent', 'interest'], state)

  if (!instruments?.questions?.length) {
    return emptyInterestQuestion(uuid, page)
  }

  const questions = instruments.questions
  const questionAtPage = R.prop(page, questions)

  if (!questionAtPage) {
    return emptyInterestQuestion(uuid, page)
  }

  const { key, text } = questionAtPage
  const { scale, prompt } = instruments
  const value = R.prop(key, answers)

  return {
    uuid,
    question: key,
    prompt,
    label: text,
    scale,
    value,
    current: page,
  }
}
